import { DataGlueGraph, NodeInstance } from '@glueapp/graph-types';
import { ValidationError, ValidationRuleResult } from '../ValidationRule';

interface EndpointDefinition {
    id: string;
    method: string;
    path: string;
}

export const ApiEndpointOverlapRule = (graph: DataGlueGraph): ValidationRuleResult => {
    const errors: ValidationError[] = [];

    // Check main graph
    errors.push(...validateEndpoints(graph.nodeInstances, 'main'));

    // Check function graphs
    if (graph.functions) {
        for (const func of graph.functions) {
            errors.push(...validateEndpoints(func.nodeInstances, func.id));
        }
    }

    return { valid: errors.length === 0, errors };
};

function normalizePath(path: string): string {
    return path
        .split('/')
        .map(segment => (segment.startsWith(':') ? '{param}' : segment))
        .join('/');
}

function methodsOverlap(methodA: string, methodB: string): boolean {
    if (methodA === methodB) return true;
    // ANY overlaps with ANY, but not with specific methods
    // "same method" means exact match only
    return false;
}

function validateEndpoints(nodeInstances: NodeInstance[], context: string): ValidationError[] {
    const errors: ValidationError[] = [];

    const apiServers = nodeInstances.filter(node => node.nodeId === 'ApiServer');

    for (const apiServer of apiServers) {
        const endpoints = (apiServer.state?.endpoints as unknown as EndpointDefinition[]) || [];

        for (let outer = 0; outer < endpoints.length; outer++) {
            for (let inner = outer + 1; inner < endpoints.length; inner++) {
                const endpointA = endpoints[outer];
                const endpointB = endpoints[inner];

                const normalizedPathA = normalizePath(endpointA.path);
                const normalizedPathB = normalizePath(endpointB.path);

                if (normalizedPathA === normalizedPathB && methodsOverlap(endpointA.method, endpointB.method)) {
                    const location =
                        context === 'main'
                            ? { nodeId: apiServer.id, context: 'main graph' }
                            : { nodeId: apiServer.id, functionId: context };
                    errors.push({
                        code: 'ENDPOINT_OVERLAP',
                        message: `Endpoints '${endpointA.method} ${endpointA.path}' and '${endpointB.method} ${endpointB.path}' overlap. Each method + path combination must be unique.`,
                        severity: 'error',
                        location,
                    });
                }
            }
        }
    }

    return errors;
}
